import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material'
import Wrapper from '@app/components/Common/Wrapper/Wrapper'
import { Add } from 'icons'
import useStyles from './VoiceAssistants.style'

type Assistant = 'amazon_alexa' | 'google_assistant'

const assistantLabels: Record<Assistant, string> = {
  amazon_alexa: 'Amazon Alexa',
  google_assistant: 'Google Assistant',
}

const googleAssistantSteps = [
  'Open the Google Home app.',
  'Go to Home Settings.',
  'Open Gemini for Home voice assistant.',
  'Open Home control.',
  'Search for Mezzo.',
  'Link devices.',
]

const alexaSteps = [
  'Open the Amazon Alexa app.',
  'Search for Mezzo.',
  'Link devices.',
]

const VoiceAssistants: React.FC = () => {
  const { classes } = useStyles()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null)

  const selectAssistant = (assistant: Assistant) => {
    setSelectedAssistant(assistant)
    setIsDialogOpen(false)
  }

  const renderAssistantCard = (assistant: Assistant, steps: Array<string>) => (
    <Wrapper className={classes.assistantCard}>
      <Wrapper className={classes.assistantHeader}>
        <Typography className={classes.assistantTitle}>
          {assistantLabels[assistant]}
        </Typography>
        <Typography className={classes.status}>
          {selectedAssistant === assistant ? 'Selected' : 'Available'}
        </Typography>
      </Wrapper>
      <Typography color="textSecondary">
        Use your voice assistant app to connect Mezzo and link devices.
      </Typography>
      {selectedAssistant === assistant && (
        <ol className={classes.steps}>
          {steps.map((step) => (
            <li className={classes.step} key={step}>
              <Typography>{step}</Typography>
            </li>
          ))}
        </ol>
      )}
    </Wrapper>
  )

  return (
    <Paper className={classes.root}>
      <Wrapper className={classes.content}>
        <Wrapper className={classes.header}>
          <Wrapper>
            <Typography className={classes.title}>
              Voice Assistants
            </Typography>
            <Typography className={classes.subtitle}>
              Connect Mezzo to a supported voice assistant and control linked devices by voice.
            </Typography>
          </Wrapper>
          <Button
            className={classes.addButton}
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIsDialogOpen(true)}
          >
            Add a voice assistant
          </Button>
        </Wrapper>

        {renderAssistantCard('amazon_alexa', alexaSteps)}
        {renderAssistantCard('google_assistant', googleAssistantSteps)}
      </Wrapper>

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Choose voice assistant</DialogTitle>
        <DialogContent>
          <List disablePadding>
            <ListItemButton
              className={classes.providerButton}
              onClick={() => selectAssistant('amazon_alexa')}
            >
              <ListItemText
                primary="Amazon Alexa"
                secondary="Connect Mezzo from the Alexa app."
              />
            </ListItemButton>
            <ListItemButton
              className={classes.providerButton}
              onClick={() => selectAssistant('google_assistant')}
            >
              <ListItemText
                primary="Google Assistant"
                secondary="Connect Mezzo from Google Home."
              />
            </ListItemButton>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default VoiceAssistants
